import { logAppEvent, MIcons, MLink, useFeatureFlag } from "@material-dev/insights-portal-ui-components/lib";
import MAlert from "@material-dev/insights-portal-ui-components/lib/ui-components/m-alert/MAlert";
import MAutocomplete from "@material-dev/insights-portal-ui-components/lib/ui-components/m-autocomplete";
import MButton from "@material-dev/insights-portal-ui-components/lib/ui-components/m-button/MButton";
import MDialog from "@material-dev/insights-portal-ui-components/lib/ui-components/m-dialog/MDialog";
import MForm, { MFormInstanceProps } from "@material-dev/insights-portal-ui-components/lib/ui-components/m-form/MForm";
import MInput from "@material-dev/insights-portal-ui-components/lib/ui-components/m-input/MInput";
import MSpinner from "@material-dev/insights-portal-ui-components/lib/ui-components/m-spinner/MSpinner";
import { FilterOptionsState } from "@mui/material";
import React, { FC, memo, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { batch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MergedProjectDto, ProjectSourceDto } from "app/ApiClient";
import { useAppDispatch, useAppSelector } from "app/store";
import { FIREBASE_EVENTS } from "const/firebase";
import { resetEncodingFilesState } from "slices/encodingFiles/encodingFilesSlice";
import {
  originProjectsSelector,
  resetOriginProjects,
  getOriginProjects,
} from "slices/originProjects/originProjectSlice";
import { NormalizedOriginProject } from "slices/originProjects/originProjectState";
import {
  checkDimensionProjectExists,
  createProject,
  projectSelector,
  resetProject,
  setAddProjectDialogOpenedState,
  setAddCompositeProjectDialogOpenedState,
  setSelectedOriginProject,
  setOpenCreateCompositeProject,
  setOpenCreateCustomDataProject,
  setOpenCreateWalrDataProject,
} from "slices/project/projectSlice";
import { createProjectFromEncoded } from "slices/projects/projectsSlice";
import { projectsDataSelector, resetProjectsData } from "slices/projectsData/projectsDataSlice";
import { projectSourcesSelector, getAllSources } from "slices/projectSources/projectSourcesSlice";
import { LoadingState } from "types";

import { useAddProjectOpened } from "../ProjectData/hooks/useAddProjectOpened";
import { originProjectFormData, ProjectFormData, projectViewModelSchema } from "./AddProjectFormModel";
import { useCreateProjectFormStyles } from "./AddProjectFormStyles";
import CreateCompositeProjectDialog from "components/CreateCompositeProjectDialog";
import BetaChip from "components/common/BetaChip/BetaChip";
import { getNormalizedOriginSourceLable } from "utils/projectSourceHelper";
import { DIMENSION_C } from "hooks/useIsCompositeProject";
import { CUSTOMDATA, WALRDATA } from "hooks";
import { CreateCustomDataProjectDialog } from "components/CreateCustomDataProjectDialog";
import { DATASETS_PATH } from "const/routes";
import { CreateWalrDataProjectDialog } from "components/CreateWalrDataProjectDialog/CreateWalrDataProjectDialog";
import { MAINTENANCE_TEAM_EMAIL } from "const/config";

export type FormicSetFieldValueType = (
  field: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  shouldValidate?: boolean | undefined,
) => void;

export const getExistingProjectMessages = (projectId: string) => (
  <div>No action has been made due to dataset ID {projectId} already existing in Data Manager. </div>
);

const AddProjectDialog: FC = memo(() => {
  useAddProjectOpened();
  const formRef = useRef<MFormInstanceProps<ProjectFormData>>(null);
  const { classes: styles } = useCreateProjectFormStyles();
  const { originProjects, processing: isOriginProjectsLoading } = useSelector(originProjectsSelector);
  const {
    isAddProjectDialogOpened,
    isAddCompositeProjectDialogOpened,
    isOpenCreateCompositeProject,
    isOpenCreateCustomDataProject,
    isOpenCreateWalrDataProject,
    createProjectLoadingState,
    createdProject,
    existingProject,
    createProjectError,
  } = useSelector(projectSelector);
  const { processing: isLoadingDictionaryData } = useSelector(projectsDataSelector);
  const { projectSources } = useSelector(projectSourcesSelector);

  const navigate = useNavigate();
  const [selectedProjectNumber, setSelectedProjectNumber] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<ProjectSourceDto | null>(null);
  const { currentUser } = useAppSelector((state) => state.userState);
  const dispatch = useAppDispatch();
  const { featureWalrDataSource } = useFeatureFlag();

  useEffect(() => {
    if (createProjectLoadingState === LoadingState.succeed && createdProject?.id) {
      dispatch(resetProject());
      dispatch(setAddProjectDialogOpenedState(false));
      navigate(`${DATASETS_PATH}/${createdProject.id ?? 0}/${createdProject.versionId ?? ""}`);
    }
  }, [createProjectLoadingState, dispatch, history, createdProject]);

  useEffect(() => {
    dispatch(getAllSources());
  }, [dispatch]);

  useEffect(() => {
    if (selectedProjectNumber) {
      const promise = dispatch(checkDimensionProjectExists(selectedProjectNumber));
      return () => {
        promise.abort?.();
      };
    }
  }, [selectedProjectNumber, dispatch]);

  const onProjectNumberChange = useCallback(
    (setFieldValue: FormicSetFieldValueType) => (e: SyntheticEvent, value: NormalizedOriginProject | null) => {
      if (!value) {
        return;
      }
      setFieldValue("project", value);
      setSelectedProjectNumber(value.name);
    },
    [setSelectedProjectNumber],
  );

  const onSourceSelected = useCallback(
    (source: ProjectSourceDto | null) => {
      if (source?.id) {
        setSelectedSource(source);
        if (source?.name === CUSTOMDATA) {
          openCreateCustomDataProject();
        }
        else if (source?.name === WALRDATA) {
          openCreateWalrDataProject();
        } else {
          dispatch(getOriginProjects(source.id));
        }
      }
    },
    [setSelectedSource],
  );

  const renderOptions = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: NormalizedOriginProject) => {
      const adornmentValue = option.sourceCode ?? option.source;
      const adornment = adornmentValue ? `(${adornmentValue})` : "";
      return (
        <li
          {...props}
          key={`${option.name}-${option.label ?? ""}-${option.environmentKey ?? ""}-${option.sourceCode ?? ""}`}>
          {option.label ? `${option.name} ${adornment} - ${option.label}` : `${option.name} ${adornment}`}
        </li>
      );
    },
    [],
  );

  const getOptionLabel = useCallback((option: NormalizedOriginProject) => option.label ?? "", []);

  useEffect(() => {
    if (!isAddProjectDialogOpened) {
      setSelectedProjectNumber(null);
      setSelectedSource(null);
    }
  }, [isAddProjectDialogOpened]);

  const onCloseDialog = useCallback(() => {
    // disallow closing on pending
    if (createProjectLoadingState === LoadingState.pending) {
      dispatch(setAddProjectDialogOpenedState(true));
    } else {
      dispatch(setAddProjectDialogOpenedState(false));
    }
  }, [createProjectLoadingState, dispatch]);

  const onCloseCompositeDialog = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(false));
  }, [dispatch]);

  const handleSubmit = useCallback(
    (values: ProjectFormData) => {
      const { project } = values;
      logAppEvent(FIREBASE_EVENTS.projectsPage.onLoadProjectClick, { projectId: project });
      if (!existingProject && selectedSource) {
        if (project) {
          batch(() => {
            dispatch(resetEncodingFilesState());
            dispatch(resetProjectsData());
            dispatch(setSelectedOriginProject(project));
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            dispatch(
              createProject({
                originProjectId: project.name,
                originProjectName: project.label,
                environmentKey: project.environmentKey,
                sourceId: selectedSource.id,
                sourceCode: project.sourceCode,
              }),
            );
          });
        }

        if (createProjectLoadingState === LoadingState.succeed) {
          dispatch(resetProject());
          onCloseDialog();
        }
      } else {
        //  Redirected to existing project in table
        dispatch(resetProject());
        if (existingProject?.id) {
          navigate(`${DATASETS_PATH}/${existingProject.id ?? 0}/${existingProject.versionId ?? ""}`);
        } else if (existingProject?.originProjectId) {
          dispatch(
            createProjectFromEncoded({
              originProjectId: existingProject?.originProjectId,
            }),
          )
            .then(({ payload }) => {
              const res = payload as MergedProjectDto;
              navigate(`${DATASETS_PATH}/${res.id ?? 0}/${res.versionId ?? ""}`);
              return;
            })
            .catch((e) => {
              console.error(e);
              return;
            });
          onCloseDialog();
        }
      }
    },
    [existingProject, createProjectLoadingState, dispatch, onCloseDialog, history, selectedSource],
  );


  const filteredProjectSources = useMemo(() => {
    let data = projectSources?.filter((source) => source.name != DIMENSION_C);
    if (!featureWalrDataSource) {
      data = data?.filter((source) => source.name !== WALRDATA);
    }
    return data;
  }, [projectSources]);

  const sourceSelect = useMemo(
    () => (
      <div className={styles.sources}>
        {filteredProjectSources &&
          filteredProjectSources.map((source) => (
            <div className="button-container">
            <MButton
              name={source.name}
              title={getNormalizedOriginSourceLable(source.displayName)}
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => onSourceSelected(source)}>
              {getNormalizedOriginSourceLable(source.displayName)}
            </MButton>
            </div>
          ))}
      </div>
    ),
    [styles, projectSources],
  );

  const getFilteredOptions = (
    options: NormalizedOriginProject[],
    state: FilterOptionsState<NormalizedOriginProject>,
  ) => {
    const normalizedInputValue = state.inputValue?.toLowerCase();
    if (normalizedInputValue) {
      return options.filter((opt) => state.getOptionLabel(opt)?.toLowerCase().includes(normalizedInputValue));
    }
    return options;
  };

  const backToSources = useCallback(() => {
    setSelectedSource(null);
    dispatch(resetOriginProjects());
  }, []);

  const projectSelect = useMemo(
    () => (
      <>
        {existingProject && selectedProjectNumber && (
          <div className={styles.message}>
            <div className={styles.messageTitle}>Dataset already exists!</div>
            <div className={styles.messageBody}>{getExistingProjectMessages(selectedProjectNumber)}</div>
          </div>
        )}
        {createProjectError && (
          <MAlert severity="error" closable>
            Something wrong, Please contact an please contact an &nbsp;
            <MLink className={styles.errorLink} href={`mailto:${MAINTENANCE_TEAM_EMAIL}`}>
              Insights Portal administrator.
            </MLink>
             for more details.
          </MAlert>
        )}
        <MForm
          formProps={{ id: "project-form" }}
          formRef={formRef}
          onSubmit={handleSubmit}
          initialValues={originProjectFormData}
          validationSchema={projectViewModelSchema}>
          {({ setFieldValue }) => (
            <>
              {!existingProject && (
                <div>
                  {isOriginProjectsLoading && <MSpinner isLoading />}
                  <MAutocomplete
                    options={originProjects}
                    onChange={onProjectNumberChange(setFieldValue)}
                    loading={isOriginProjectsLoading}
                    key={isOriginProjectsLoading ? "project" : "project-loaded"}
                    defaultValue={originProjectFormData.project}
                    filterOptions={getFilteredOptions}
                    renderInput={(params) => (
                      <MInput
                        {...params}
                        placeholder={"Type or select a dataset ID"}
                        name="project"
                        variant="outlined"
                      />
                    )}
                    renderOption={renderOptions}
                    getOptionLabel={getOptionLabel}
                    className={styles.projectIdField}
                  />
                </div>
              )}
            </>
          )}
        </MForm>
      </>
    ),
    [originProjects, createProjectError, existingProject, handleSubmit, isOriginProjectsLoading, onProjectNumberChange],
  );

  const actions = useMemo(() => {
    if (existingProject) {
      return (
        <div className={styles.actions}>
          <MButton variant="outlined" color="primary" type="submit" size="small" form="project-form">
            Open Existing Dataset
          </MButton>
        </div>
      );
    }

    if (selectedSource) {
      return (
        <div className={styles.actions}>
          <MButton color="primary" type="button" variant="contained" size="small" onClick={backToSources}>
            Back to Data Source
          </MButton>
          <MButton
            disabled={isOriginProjectsLoading || isLoadingDictionaryData}
            color="primary"
            type="submit"
            variant="outlined"
            size="small"
            form="project-form">
            Load dataset
          </MButton>
        </div>
      );
    }
    return undefined;
  }, [selectedSource, isOriginProjectsLoading, isLoadingDictionaryData, styles, existingProject]);

  const title = useMemo(() => {
    if (!selectedSource) {
      return "Select your survey data source";
    }

    return existingProject?.id ? "" : "Enter your dataset ID";
  }, [existingProject, selectedSource]);

  const openSourceFromDatabase = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(false));
    dispatch(setAddProjectDialogOpenedState(true));
  }, [dispatch]);

  const openCreateCompositeProject = useCallback(() => {
    dispatch(setAddCompositeProjectDialogOpenedState(true));
    dispatch(setOpenCreateCompositeProject(true));
  }, [dispatch]);

  const openCreateCustomDataProject = useCallback(() => {
    dispatch(setAddProjectDialogOpenedState(false));
    dispatch(setOpenCreateCustomDataProject(true));
  }, [dispatch]);

  const openCreateWalrDataProject = useCallback(() => {
    dispatch(setAddProjectDialogOpenedState(false));
    dispatch(setOpenCreateWalrDataProject(true));
  }, [dispatch]);

  const handleBackClick = () => {
    dispatch(setAddProjectDialogOpenedState(false));
    if (currentUser?.isAdmin) 
      dispatch(setAddCompositeProjectDialogOpenedState(true));
    else 
      dispatch(setAddCompositeProjectDialogOpenedState(false));
  };

  const onOpen = useCallback(() => {
    if (currentUser?.isAdmin) {
      dispatch(setAddCompositeProjectDialogOpenedState(true));
    } else {
      dispatch(setAddProjectDialogOpenedState(true));
    }
  }, [dispatch, currentUser]);

  return (
    <>
      <MButton variant="outlined" color="primary" className={styles.addDatasetBtn} onClick={onOpen}>
        Add Dataset
      </MButton>
      <MDialog
        className={styles.sourceSelectionDialog}
        open={Boolean(isAddProjectDialogOpened)}
        onClose={onCloseDialog}
        fullWidth
        actionsElement={actions}
        titleElement={
            <div className={styles.box}>
              {(currentUser?.isAdmin) &&
                <MButton className={styles.btnBack} onClick={handleBackClick}>
                    <MIcons.ArrowBack className={styles.backIcon} />
                     Go back
                </MButton>
              }
              <div className={styles.title}>{title}</div>
            </div>
        }>
      <div className={styles.root}>{selectedSource ? projectSelect : sourceSelect}</div>
      </MDialog>

      <MDialog
        className={styles.dialog}
        open={Boolean(isAddCompositeProjectDialogOpened)}
        onClose={onCloseCompositeDialog}
        titleElement={<h4 className={styles.dialogTitle}>Select how you want to add a dataset</h4>}
        maxWidth={"md"}
        fullWidth>
        <div className={styles.content}>
          <div className={styles.item} onClick={() => openSourceFromDatabase()}>
            <div className={styles.info}>
               
                <MIcons.DatasourceIcon className={styles.dataIcon} />
                <div className={styles.infoSpan}><span>Add from Data Source</span></div>
              
            </div>
          </div>
          <div className={styles.item} onClick={() => openCreateCompositeProject()}>
            <div className={styles.info}>
                <MIcons.CompositeDatasetIcon className={styles.dataIcon} />
                <div><span>Create a composite dataset</span></div>
              </div>
            <div className={styles.description}>A composite dataset is a merged dataset</div>
            <div><BetaChip /></div>
          </div>
        </div>
      </MDialog>
      <CreateCompositeProjectDialog open={isOpenCreateCompositeProject} isWorkdayDataset= {false}/>
      <CreateCustomDataProjectDialog open={isOpenCreateCustomDataProject} isWorkdayDataset= {false} />
      <CreateWalrDataProjectDialog open={isOpenCreateWalrDataProject} isWorkdayDataset= {false} />
    </>
  );
});

export default AddProjectDialog;
